import { prefersReducedMotion, onDocumentReady } from '@theme/utilities';

/**
 * Global scroll-triggered reveal system for GripSway.
 *
 * Reveals top-level page sections with a subtle fade-in, and staggers
 * product/category card reveals within resource-list grids (homepage rows,
 * product recommendations, cart cross-sell). Deliberately leaves
 * `.product-grid__item` (collection/search grids) untouched — those already
 * get an entrance transition from `results-list[initialized]` in
 * product-grid.liquid, and this module only adds a stagger to that existing
 * rule via CSS, not a second competing observer.
 *
 * One shared IntersectionObserver drives every reveal (cheap, no per-element
 * observers). Elements already on/near screen at scan time are revealed
 * immediately with no transition, so nothing flashes hidden-then-visible.
 *
 * Re-scans on `shopify:section:load` (Theme Editor add/reorder) and via a
 * MutationObserver on #MainContent (infinite scroll, async product
 * recommendations, AJAX filter/sort re-renders) so newly inserted markup is
 * picked up without a second init pass.
 */

const REVEAL_ATTR = 'data-scroll-reveal';
const VISIBLE_CLASS = 'scroll-reveal--visible';
const SECTION_SELECTOR = '#MainContent > .shopify-section';
const STAGGER_SELECTOR = '.resource-list__item';
const MAX_STAGGER_STEPS = 8;
const NEAR_VIEWPORT_MARGIN = 200;

/**
 * Per-intensity stagger, matching the token sets in scroll-animation-styles.liquid.
 * Read from `<body data-motion>` so the delay between sibling cards stays in
 * proportion to the reveal duration the CSS is using.
 */
const STAGGER_STEP_MS = { subtle: 40, standard: 55, expressive: 80 };

/**
 * @returns {number} Milliseconds between successive cards in one grid.
 */
function staggerStep() {
  const motion = document.body.dataset.motion ?? 'standard';
  return STAGGER_STEP_MS[/** @type {keyof typeof STAGGER_STEP_MS} */ (motion)] ?? STAGGER_STEP_MS.standard;
}

/** @type {WeakSet<Element>} */
const processed = new WeakSet();

/** @type {IntersectionObserver | undefined} */
let observer;

/**
 * @returns {IntersectionObserver}
 */
function getObserver() {
  if (observer) return observer;

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        reveal(/** @type {HTMLElement} */ (entry.target));
        observer?.unobserve(entry.target);
      }
    },
    { rootMargin: `0px 0px -8% 0px`, threshold: 0.05 }
  );

  return observer;
}

/**
 * @param {Element} element
 * @returns {boolean}
 */
function isNearViewport(element) {
  const rect = element.getBoundingClientRect();
  return rect.top < window.innerHeight + NEAR_VIEWPORT_MARGIN && rect.bottom > -NEAR_VIEWPORT_MARGIN;
}

/**
 * @param {HTMLElement} element
 */
function reveal(element) {
  element.classList.add(VISIBLE_CLASS);

  /**
   * @param {Event} event
   */
  function onTransitionEnd(event) {
    // `transitionend` bubbles, and a `fade-up` card's nested image runs its own
    // (slower) transform transition — ignore bubbled events so cleanup only runs
    // once this element's own transition finishes, not the image's. Using a manual
    // listener (rather than `{ once: true }`) means a bubbled event doesn't consume
    // the one-shot slot before the real target has fired.
    if (event.target !== element) return;

    element.removeEventListener('transitionend', onTransitionEnd);

    // Once the reveal has played, drop every reveal-related attribute/class so the
    // element is left with no lingering `transition`/`will-change`, matching the
    // codebase's existing will-change cleanup pattern in utilities.js.
    element.removeAttribute(REVEAL_ATTR);
    element.classList.remove(VISIBLE_CLASS);
    element.style.removeProperty('--scroll-reveal-delay');
  }

  element.addEventListener('transitionend', onTransitionEnd);
}

/**
 * @param {Element} element
 * @param {'fade-in' | 'fade-up'} variant
 * @param {number | null} staggerIndex
 */
function prepare(element, variant, staggerIndex) {
  if (processed.has(element)) return;
  processed.add(element);

  if (isNearViewport(element)) {
    // Already visible (or about to be) at scan time — skip the hidden state entirely
    // rather than risk a flash of hidden content before the observer's first callback.
    return;
  }

  const el = /** @type {HTMLElement} */ (element);
  el.setAttribute(REVEAL_ATTR, variant);

  if (staggerIndex != null && staggerIndex > 0) {
    const steps = Math.min(staggerIndex, MAX_STAGGER_STEPS);
    el.style.setProperty('--scroll-reveal-delay', `${steps * staggerStep()}ms`);
  }

  getObserver().observe(el);
}

function scan() {
  const main = document.getElementById('MainContent');
  if (!main) return;

  for (const section of main.querySelectorAll(SECTION_SELECTOR)) {
    prepare(section, 'fade-in', null);
  }

  // Stagger index is computed per parent so unrelated grids on the same page
  // (e.g. two resource-list rows) each start their own count from zero.
  /** @type {Map<Element, number>} */
  const siblingCounts = new Map();

  for (const item of main.querySelectorAll(STAGGER_SELECTOR)) {
    const parent = item.parentElement;
    if (!parent) continue;

    const index = siblingCounts.get(parent) ?? 0;
    siblingCounts.set(parent, index + 1);

    prepare(item, 'fade-up', index);
  }
}

/**
 * @param {() => void} fn
 * @param {number} delay
 */
function debounce(fn, delay) {
  /** @type {ReturnType<typeof setTimeout>} */
  let timer;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(fn, delay);
  };
}

function init() {
  if (prefersReducedMotion()) return;

  const main = document.getElementById('MainContent');
  if (!main) return;

  scan();

  document.addEventListener('shopify:section:load', scan);

  const mutationObserver = new MutationObserver(debounce(scan, 150));
  mutationObserver.observe(main, { childList: true, subtree: true });
}

onDocumentReady(init);
